import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useFiles from "../../api/files";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Download,
  File,
  FileText,
  Image,
  FileArchive,
  AlertCircle,
  Check,
  FolderOpen,
  List,
  Grid3x3,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
  X,
} from "lucide-react";

const FilesViewer = () => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { getCurrentCompanyFiles, downloadFile, getFilePreviewUrl } =
    useFiles();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "list" or "grid"
  const [imagePreviews, setImagePreviews] = useState({}); // {fileId: previewUrl}
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all", "images", "documents", "archives"
  const [sortBy, setSortBy] = useState("date-desc"); // "date-desc", "date-asc", "name-asc", "name-desc", "size-asc", "size-desc"

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const data = await getCurrentCompanyFiles();
      console.log("Fetched files data:", data);
      // Ensure data is always an array
      const filesList = Array.isArray(data) ? data : [];
      setFiles(filesList);
      // Load image previews for image files
      loadImagePreviews(filesList);
    } catch (error) {
      console.error("Error fetching files:", error);
      setError("Kon bestanden niet ophalen");
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadImagePreviews = async (filesList) => {
    const previews = {};
    for (const file of filesList) {
      if (isImageFile(file.filename)) {
        try {
          const url = await getFilePreviewUrl(file.id);
          if (url) {
            previews[file.id] = url;
          }
        } catch (err) {
          console.error("Error loading preview for", file.filename, err);
        }
      }
    }
    setImagePreviews(previews);
  };

  const isImageFile = (filename) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".svg",
      ".bmp",
    ];
    return imageExtensions.some((ext) => filename.toLowerCase().endsWith(ext));
  };

  const handleDownload = async (fileId) => {
    try {
      setSuccess("");
      setError("");
      const ok = await downloadFile(fileId);
      if (ok) {
        setSuccess("Download gestart (S3 URL)...");
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error.message || "Kon bestand niet downloaden");
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
      return (
        <Image
          className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`}
        />
      );
    }
    if (["pdf"].includes(ext)) {
      return (
        <FileText
          className={`h-4 w-4 ${isDark ? "text-red-400" : "text-red-500"}`}
        />
      );
    }
    if (["zip", "rar", "7z"].includes(ext)) {
      return (
        <FileArchive
          className={`h-4 w-4 ${isDark ? "text-amber-400" : "text-amber-500"}`}
        />
      );
    }
    return (
      <File
        className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
      />
    );
  };

  const getFileIconLarge = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
      return (
        <Image
          className={`h-12 w-12 ${isDark ? "text-blue-400" : "text-blue-500"}`}
        />
      );
    }
    if (["pdf"].includes(ext)) {
      return (
        <FileText
          className={`h-12 w-12 ${isDark ? "text-red-400" : "text-red-500"}`}
        />
      );
    }
    if (["zip", "rar", "7z"].includes(ext)) {
      return (
        <FileArchive
          className={`h-12 w-12 ${isDark ? "text-amber-400" : "text-amber-500"}`}
        />
      );
    }
    return (
      <File
        className={`h-12 w-12 ${isDark ? "text-gray-600" : "text-gray-400"}`}
      />
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getFileType = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext))
      return "images";
    if (["pdf", "doc", "docx", "txt", "xls", "xlsx"].includes(ext))
      return "documents";
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "archives";
    return "other";
  };

  // Filter and search files
  const getFilteredAndSortedFiles = () => {
    let filtered = [...files];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (file) =>
          file.filename.toLowerCase().includes(query) ||
          (file.description && file.description.toLowerCase().includes(query)),
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(
        (file) => getFileType(file.filename) === filterType,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date-desc":
          return (
            new Date(b.uploadedAt || b.created_at) -
            new Date(a.uploadedAt || a.created_at)
          );
        case "date-asc":
          return (
            new Date(a.uploadedAt || a.created_at) -
            new Date(b.uploadedAt || b.created_at)
          );
        case "name-asc":
          return (a.description || a.filename).localeCompare(
            b.description || b.filename,
          );
        case "name-desc":
          return (b.description || b.filename).localeCompare(
            a.description || a.filename,
          );
        case "size-asc":
          return a.filesize - b.filesize;
        case "size-desc":
          return b.filesize - a.filesize;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredFiles = getFilteredAndSortedFiles();

  return (
    <div className="space-y-5">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Bestanden
          </h1>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            Download bestanden die voor u beschikbaar zijn gesteld.
          </p>
        </div>
        <div className="flex gap-1">
          {[
            { mode: "list", icon: List },
            { mode: "grid", icon: Grid3x3 },
          ].map(({ mode, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                viewMode === mode
                  ? isDark
                    ? "bg-white text-black"
                    : "bg-gray-900 text-white"
                  : isDark
                    ? "text-gray-500 hover:text-white hover:bg-white/[0.06]"
                    : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>

      {/* Search, Filter and Sort Bar */}
      <div
        className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder="Zoek op naam of beschrijving..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full h-9 pl-9 pr-9 rounded-lg text-[13px] outline-none transition-colors ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-white placeholder-gray-600 focus:border-white/[0.12]" : "bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger
              className={`w-full md:w-[180px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <SelectValue placeholder="Filter type" />
              </div>
            </SelectTrigger>
            <SelectContent
              className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
            >
              <SelectItem value="all">Alle types</SelectItem>
              <SelectItem value="images">Afbeeldingen</SelectItem>
              <SelectItem value="documents">Documenten</SelectItem>
              <SelectItem value="archives">Archieven</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              className={`w-full md:w-[180px] h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
            >
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-3.5 w-3.5" />
                <SelectValue placeholder="Sorteer op" />
              </div>
            </SelectTrigger>
            <SelectContent
              className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
            >
              <SelectItem value="date-desc">Nieuwste eerst</SelectItem>
              <SelectItem value="date-asc">Oudste eerst</SelectItem>
              <SelectItem value="name-asc">Naam A-Z</SelectItem>
              <SelectItem value="name-desc">Naam Z-A</SelectItem>
              <SelectItem value="size-asc">Kleinste eerst</SelectItem>
              <SelectItem value="size-desc">Grootste eerst</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active Filters */}
        {(searchQuery || filterType !== "all") && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span
              className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              {filteredFiles.length} van {files.length} bestanden
            </span>
            {searchQuery && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
              >
                Zoeken: "{searchQuery}"
                <button
                  onClick={() => setSearchQuery("")}
                  className="hover:opacity-70"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
            {filterType !== "all" && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
              >
                Type:{" "}
                {filterType === "images"
                  ? "Afbeeldingen"
                  : filterType === "documents"
                    ? "Documenten"
                    : "Archieven"}
                <button
                  onClick={() => setFilterType("all")}
                  className="hover:opacity-70"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Notifications */}
      {error && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}
        >
          <Check className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Stats */}
      <div
        className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        <div className="flex items-center gap-1.5 mb-2">
          <FolderOpen
            className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
          />
          <span
            className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Beschikbare bestanden
          </span>
        </div>
        <span
          className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
        >
          {files.length}
        </span>
      </div>

      {/* Files Content */}
      <div
        className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
      >
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div
              className={`w-8 h-8 rounded-full border-2 animate-spin ${isDark ? "border-white/[0.08] border-t-white/40" : "border-gray-200 border-t-gray-600"}`}
            />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div
              className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
            >
              <FolderOpen
                className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
            </div>
            <h3
              className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
            >
              Geen bestanden beschikbaar
            </h3>
            <p
              className={`text-[13px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              Er zijn momenteel geen bestanden voor u beschikbaar gesteld.
            </p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div
              className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
            >
              <Search
                className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              />
            </div>
            <h3
              className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
            >
              Geen bestanden gevonden
            </h3>
            <p
              className={`text-[13px] mb-4 ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              Probeer andere zoektermen of pas uw filters aan.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setFilterType("all");
              }}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Reset filters
            </button>
          </div>
        ) : viewMode === "list" ? (
          <div>
            {/* Table Header */}
            <div
              className={`grid grid-cols-[auto_1fr_100px_140px_100px] gap-4 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider ${isDark ? "bg-white/[0.02] text-gray-600 border-b border-white/[0.04]" : "bg-gray-50/80 text-gray-500 border-b border-gray-100"}`}
            >
              <div className="w-5" />
              <div>Beschrijving</div>
              <div>Grootte</div>
              <div>Toegevoegd</div>
              <div className="text-right">Actie</div>
            </div>
            {/* Table Rows */}
            {filteredFiles.map((file, i) => (
              <div
                key={file.id}
                className={`grid grid-cols-[auto_1fr_100px_140px_100px] gap-4 items-center px-4 py-3 ${i !== filteredFiles.length - 1 ? (isDark ? "border-b border-white/[0.04]" : "border-b border-gray-100") : ""} ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"} transition-colors`}
              >
                <div className="w-5">{getFileIcon(file.filename)}</div>
                <div className="min-w-0">
                  <span
                    className={`text-[13px] font-medium truncate block ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {file.description || file.filename}
                  </span>
                  {file.description && (
                    <span
                      className={`text-[11px] truncate block ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      {file.filename}
                    </span>
                  )}
                </div>
                <span
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {formatFileSize(file.filesize)}
                </span>
                <span
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                >
                  {formatDate(file.uploadedAt || file.created_at)}
                </span>
                <div className="text-right">
                  <button
                    onClick={() => handleDownload(file.id)}
                    className={`h-7 px-2.5 rounded-lg text-[11px] font-medium inline-flex items-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    <Download className="h-3 w-3" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredFiles.map((file) => {
              const isImage = isImageFile(file.filename);
              const previewUrl = imagePreviews[file.id];

              return (
                <div
                  key={file.id}
                  className={`group rounded-xl overflow-hidden transition-all ${isDark ? "bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12]" : "bg-gray-50/50 border border-gray-200/80 hover:border-gray-300"}`}
                >
                  <div
                    className={`relative h-36 flex items-center justify-center overflow-hidden ${isDark ? "bg-white/[0.02]" : "bg-gray-50"}`}
                  >
                    {isImage && previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={file.description || file.filename}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      getFileIconLarge(file.filename)
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <div>
                      <h3
                        className={`text-[13px] font-medium line-clamp-2 ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {file.description || file.filename}
                      </h3>
                      {file.description && (
                        <p
                          className={`text-[11px] truncate mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                        >
                          {file.filename}
                        </p>
                      )}
                    </div>
                    <div
                      className={`flex items-center justify-between text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      <span>{formatFileSize(file.filesize)}</span>
                      <span>
                        {
                          formatDate(file.uploadedAt || file.created_at).split(
                            ",",
                          )[0]
                        }
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(file.id)}
                      className={`w-full h-8 rounded-lg text-[12px] font-medium inline-flex items-center justify-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilesViewer;
